import React, { useEffect, useState } from 'react';
import { getUsersByServer, editUserInfo, deleteUser, registerUser } from '../services/firebase';
import { Table, Form, Button, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './UsersList.css';
import * as XLSX from 'xlsx';

const UsersList = ({ serverName }) => {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', password: '' });
  const [newUser, setNewUser] = useState({ userId: '', password: '', name: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [serverName]);

  const fetchUsers = async () => {
    const result = await getUsersByServer(serverName);
    if (result.success) {
      const filteredUsers = result.usersList.filter(user => user.level >= 2);
      setUsers(filteredUsers);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const filteredUsers = sortedUsers.filter(user =>
    Object.values(user).some(value => 
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const paginatedUsers = filteredUsers.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name, password: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const updates = { name: editForm.name };
      if (editForm.password) updates.password = editForm.password;
      
      const result = await editUserInfo(editingUser.id, updates);
      if (result.success) {
        setShowModal(false);
        setEditingUser(null);
        await fetchUsers();
        toast.success('Usuario actualizado exitosamente');
      } else {
        toast.error(result.message || 'Error al actualizar usuario');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar usuario');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este usuario?')) {
      try {
        const result = await deleteUser(userId);
        if (result.success) {
          await fetchUsers();
          toast.success('Usuario eliminado exitosamente');
        } else {
          toast.error(result.message || 'Error al eliminar usuario');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al eliminar usuario');
      }
    }
  };

  const handleAddUser = async () => {
    try {
      if (!newUser.userId || !newUser.password || !newUser.name) {
        toast.error('Todos los campos son obligatorios');
        return;
      }

      const result = await registerUser(
        newUser.userId,
        newUser.password,
        newUser.name,
        serverName,
        2
      );

      if (result.success) {
        setShowModal(false);
        setNewUser({ userId: '', password: '', name: '' });
        await fetchUsers();
        toast.success('Usuario creado exitosamente');
      } else {
        toast.error(result.message || 'Error al crear usuario');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al crear usuario');
    }
  };

  const handleDownloadExcel = () => {
    try {
      const dataToExport = users.map(user => ({
        'ID Usuario': user.userId,
        'Sucursal': user.name,
        'Contraseña': showPasswords ? user.password : '••••••••'
      }));

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Usuarios');
      
      const fileName = `usuarios_${serverName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      
      toast.success('Archivo descargado exitosamente');
    } catch (error) {
      console.error('Error al descargar Excel:', error);
      toast.error('Error al descargar el archivo');
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    setShowModal(true);
  };

  return (
    <div className="datatable-container">
      <div className="datatable-header">
        <h2>Gestión de Usuarios</h2>
        <div className="datatable-actions">
          <div className="search-box">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="action-buttons">
            <Button 
              className="download-btn"
              onClick={handleDownloadExcel}
              title="Descargar lista de usuarios"
            >
              <i className="fas fa-file-excel"></i>
              <span>Descargar Excel</span>
            </Button>
            <button className="action-btn add" onClick={handleAdd}>
              <i className="fas fa-user-plus"></i>
              Agregar Usuario
            </button>
          </div>
        </div>
      </div>

      <div className="datatable-wrapper">
        <Table hover className="datatable">
          <thead>
            <tr>
              <th className="column-id">ID Usuario</th>
              <th className="column-name">Nombre</th>
              <th className="column-password">
                Contraseña
                <button
                  type="button"
                  className="password-toggle-all"
                  onClick={() => setShowPasswords(!showPasswords)}
                  title={showPasswords ? "Ocultar contraseñas" : "Mostrar contraseñas"}
                >
                  <i className={`fas ${showPasswords ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </th>
              <th className="column-actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => (
              <tr key={user.id}>
                <td className="column-id">{user.userId}</td>
                <td className="column-name">{user.name}</td>
                <td className="column-password">
                  {showPasswords ? user.password : '••••••••'}
                </td>
                <td className="column-actions">
                  <div className="action-buttons">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="action-btn"
                      onClick={() => handleEdit(user)}
                      title="Cambiar contraseña"
                    >
                      <i className="fas fa-key"></i>
                      <span>Contraseña</span>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="action-btn"
                      onClick={() => handleDelete(user.id)}
                      title="Eliminar usuario"
                    >
                      <i className="fas fa-trash"></i>
                      <span>Eliminar</span>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="datatable-footer">
        <div className="rows-per-page">
          <span>Mostrar:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>registros</span>
        </div>
        <div className="pagination-controls">
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(0)}
            disabled={currentPage === 0}
            title="Primera página"
          >
            <i className="fas fa-step-backward" style={{ color: '#fff' }}></i>
          </Button>
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            title="Página anterior"
          >
            <i className="fas fa-chevron-left" style={{ color: '#fff' }}></i>
          </Button>
          <span className="page-info">
            Página {currentPage + 1} de {Math.ceil(filteredUsers.length / rowsPerPage)}
          </span>
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={(currentPage + 1) * rowsPerPage >= filteredUsers.length}
            title="Página siguiente"
          >
            <i className="fas fa-chevron-right" style={{ color: '#fff' }}></i>
          </Button>
          <Button
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.ceil(filteredUsers.length / rowsPerPage) - 1)}
            disabled={(currentPage + 1) * rowsPerPage >= filteredUsers.length}
            title="Última página"
          >
            <i className="fas fa-step-forward" style={{ color: '#fff' }}></i>
          </Button>
        </div>
      </div>

      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        backdrop={false}
        keyboard={true}
        centered
        className="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {!editingUser && (
              <Form.Group className="mb-3">
                <Form.Label>ID de Usuario</Form.Label>
                <Form.Control
                  type="text"
                  value={newUser.userId}
                  onChange={(e) =>
                    setNewUser({ ...newUser, userId: e.target.value })
                  }
                  placeholder="Ingrese ID de usuario"
                  autoFocus
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                value={editingUser ? editForm.name : newUser.name}
                onChange={(e) =>
                  editingUser
                    ? setEditForm({ ...editForm, name: e.target.value })
                    : setNewUser({ ...newUser, name: e.target.value })
                }
                placeholder="Ingrese nombre completo"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                {editingUser ? 'Nueva Contraseña' : 'Contraseña'}
              </Form.Label>
              <div className="password-input-group">
                <Form.Control
                  type={editingUser ? (showNewPassword ? "text" : "password") : (showPassword ? "text" : "password")}
                  value={editingUser ? editForm.password : newUser.password}
                  onChange={(e) =>
                    editingUser
                      ? setEditForm({ ...editForm, password: e.target.value })
                      : setNewUser({ ...newUser, password: e.target.value })
                  }
                  placeholder={editingUser ? 'Dejar en blanco para mantener' : 'Ingrese contraseña'}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => editingUser ? setShowNewPassword(!showNewPassword) : setShowPassword(!showPassword)}
                >
                  <i className={`fas ${editingUser ? (showNewPassword ? 'fa-eye-slash' : 'fa-eye') : (showPassword ? 'fa-eye-slash' : 'fa-eye')}`}></i>
                </button>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowModal(false);
              setEditingUser(null);
              setEditForm({ name: '', password: '' });
              setNewUser({ userId: '', password: '', name: '' });
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (editingUser) {
                handleSave();
              } else {
                handleAddUser();
              }
            }}
          >
            {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersList;
